import { useEffect, useRef } from 'react';
import {
    ExternalApplicationResponse,
    FinancingApi,
    LoanApplicationSubmitSource2,
} from '@servicetitan/financing-api-client';
import {
    ApplicationDataStore,
    ExternalApplicationStore,
    prepareExternalApplicationFromApplicantData,
    SubmittingUserStore,
} from '@servicetitan/financing-application-shared';
import { FinancedAmountStore, SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import { useDependencies } from '@servicetitan/react-ioc';
import { useMutation } from '@tanstack/react-query';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';
import { ServerError } from '@servicetitan/financing-utils-shared';
import { Log } from '@servicetitan/log-service';

export interface UseWellsFargoPrequalSubmissionOptions {
    autoSubmit?: boolean;
    onMutate?: () => void;
    onSuccess?: (data: ExternalApplicationResponse) => void;
    onError?: (error: ServerError) => void;
}

export interface UseWellsFargoPrequalSubmissionReturn {
    submit: () => void;
    isPending: boolean;
    isSuccess: boolean;
    data: ExternalApplicationResponse | undefined;
    error: ServerError | null;
    reset: () => void;
}

export const useWellsFargoPrequalSubmission = (
    options: UseWellsFargoPrequalSubmissionOptions = {},
): UseWellsFargoPrequalSubmissionReturn => {
    const { autoSubmit = true, onMutate, onSuccess, onError } = options;

    const [financingApi, log] = useDependencies(FinancingApi, Log);
    const { applicationData } = ApplicationDataStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { financedAmount } = FinancedAmountStore.useStore();
    const { selectedPlan } = SelectedPlanStore.useStore();
    const { submittingUser } = SubmittingUserStore.useStore();
    const { setExternalApplicationData } = ExternalApplicationStore.useStore();

    const hasSubmittedRef = useRef(false);

    const source =
        setupData?.role === 'technician'
            ? LoanApplicationSubmitSource2.Mobile
            : LoanApplicationSubmitSource2.OnlineEstimates;

    const mutation = useMutation<ExternalApplicationResponse, ServerError>({
        mutationKey: ['wellsFargoPrequalSubmission'],
        mutationFn: async () => {
            const request = prepareExternalApplicationFromApplicantData({
                applicationData,
                setupData: {
                    tenantId: setupData!.tenantId,
                    provider: setupData!.provider,
                    buId: setupData!.buId,
                    jobId: setupData?.jobId,
                    customerId: setupData?.customerId,
                    technicianId: setupData?.technicianId,
                    prequalificationId: setupData?.prequalificationId,
                    sourceId: undefined,
                    state: setupData?.state,
                },
                financedAmount,
                selectedPlan,
                source,
                submittingUser,
                isPrequalification: true,
            });
            log.info({
                category: 'Financing.WellsFargoPrequal',
                message: 'Submitting WF prequalification',
                data: {
                    providerName: request.providerName ?? '',
                    tenantId: request.tenantId ?? '',
                    buId: request.buId ?? '',
                    amount: request.amount ?? '',
                    jobId: request.jobId ?? '',
                    customerId: request.customerId ?? '',
                    planNumber: request.planNumber ?? '',
                    isPrequalification: request.isPrequalification,
                },
            });
            const response = await financingApi.loansSubmitExternalApplication(request);
            return response.data;
        },
        onMutate: () => {
            onMutate?.();
        },
        onSuccess: data => {
            setExternalApplicationData({
                loanId: data.id,
                accessLink:
                    data.prequalificationOutcome === 'approved' ? data.accessLink : undefined,
            });
            onSuccess?.(data);
        },
        onError: error => {
            hasSubmittedRef.current = false;
            onError?.(error);
        },
    });

    useEffect(() => {
        if (autoSubmit && !hasSubmittedRef.current) {
            hasSubmittedRef.current = true;
            mutation.mutate();
        }
    }, [autoSubmit]); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = () => {
        if (!hasSubmittedRef.current) {
            hasSubmittedRef.current = true;
            mutation.mutate();
        }
    };

    return {
        submit,
        isPending: mutation.isPending,
        isSuccess: mutation.isSuccess,
        data: mutation.data,
        error: mutation.error,
        reset: () => {
            hasSubmittedRef.current = false;
            mutation.reset();
        },
    };
};
